/**
 * Create a tournament — single-page flow consistent with match setup.
 */
import { useEffect, useState } from "react";
import type { RuleSet } from "@sportsos/engine";
import { api } from "../api.ts";
import { connectionMessage, isUnreachable, offlineNotice } from "./connection.ts";
import { useOnline } from "./useOnline.ts";
import { describeFormat, suggestedMaxPerBowler, withoutId } from "../match/formats.ts";

interface Props {
  readonly ruleSets: readonly RuleSet[];
  readonly onCreated: (id: string) => void;
  readonly onBack: () => void;
}

export function TournamentCreate({ ruleSets, onCreated, onBack }: Props): React.ReactElement {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState("");
  const [format, setFormat] = useState<"round_robin" | "groups_knockout">("round_robin");
  const [groupCount, setGroupCount] = useState<number | "">(2);
  const [ruleSetId, setRuleSetId] = useState(ruleSets[0]?.id ?? "");
  const [isPublic, setIsPublic] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The server makes the tournament, like a match: with no connection the page says so as it
  // opens and holds the button back. A request that fails anyway is still explained on submit.
  const online = useOnline();
  const [custom, setCustom] = useState(false);
  const [customOvers, setCustomOvers] = useState(20);
  const [customPlayers, setCustomPlayers] = useState(11);
  const [customMaxPerBowler, setCustomMaxPerBowler] = useState(4);

  useEffect(() => {
    if (ruleSets.length > 0 && !ruleSetId) setRuleSetId(ruleSets[0]!.id);
  }, [ruleSets]);

  const submit = async (): Promise<void> => {
    if (!name.trim() || !startDate || !endDate || (!ruleSetId && !custom)) return;
    setSubmitting(true);
    setError(null);
    try {
      let finalRuleSetId = ruleSetId;

      if (custom) {
        const created = await api.createRuleSet({
          name: `${name.trim()} Format`,
          overs: customOvers,
          ballsPerOver: 6,
          playersPerSide: customPlayers,
          maxOversPerBowler: customMaxPerBowler,
          lbwEnabled: true,
          noBallFreeHit: true,
          wideRule: "standard",
          superOverOnTie: true,
          lastManStands: false,
          oneTipOneHandOut: false,
          oneBounceCatchOut: false,
        });
        finalRuleSetId = created.id;
      }

      const tournament = await api.createTournament({
        name: name.trim(),
        description: description.trim() || null,
        startDate,
        endDate,
        format,
        ruleSetId: finalRuleSetId as any,
        groupCount: format === "groups_knockout" ? (groupCount || 2) : null,
        public: isPublic,
      });
      onCreated(tournament.id);
    } catch (err: unknown) {
      setError(
        isUnreachable(err)
          ? connectionMessage("Creating a tournament")
          : err instanceof Error
            ? err.message
            : "Failed to create tournament",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="setup-page">
      <header className="ds-appbar">
        <button className="ds-iconbtn" aria-label="Back" onClick={onBack}>
          <svg viewBox="0 0 24 24"><path d="m14 6-6 6 6 6" /></svg>
        </button>
        <span className="ds-appbar__body">
          <b className="ds-appbar__title">Create tournament</b>
        </span>
      </header>

      {!online && (
        <p className="ds-hint ds-hint--error" role="status" style={{ marginBottom: "var(--ds-space-3)" }}>
          {offlineNotice("Creating a tournament")}
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--ds-space-5)" }}>
        {/* Tournament name */}
        <div className="ds-field">
          <label className="ds-label" htmlFor="ct-name">Tournament name</label>
          <input
            className="ds-input"
            id="ct-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Weekend Warriors Cup"
          />
        </div>

        {/* Description */}
        <div className="ds-field">
          <label className="ds-label" htmlFor="ct-desc">Description <span style={{ textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
          <input
            className="ds-input"
            id="ct-desc"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Annual corporate cricket tournament"
          />
        </div>

        {/* Dates */}
        <div className="ds-field">
          <label className="ds-label" htmlFor="ct-start">Start date</label>
          <input
            className="ds-input"
            id="ct-start"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="ds-field">
          <label className="ds-label" htmlFor="ct-end">End date</label>
          <input
            className="ds-input"
            id="ct-end"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        {/* Tournament format */}
        <div>
          <p className="ds-label" style={{ marginBottom: "var(--ds-space-2)" }}>Tournament format</p>
          <div className="ds-options">
            <button
              className="ds-option"
              aria-pressed={format === "round_robin"}
              onClick={() => setFormat("round_robin")}
            >
              <span className="ds-option__value">Round Robin</span>
            </button>
            <button
              className="ds-option"
              aria-pressed={format === "groups_knockout"}
              onClick={() => setFormat("groups_knockout")}
            >
              <span className="ds-option__value">Groups + Knockout</span>
            </button>
          </div>
          {format === "groups_knockout" ? (
            <div className="ds-field" style={{ marginTop: "var(--ds-space-3)" }}>
              <label className="ds-label" htmlFor="ct-groups">Number of groups</label>
              <input
                className="ds-input"
                id="ct-groups"
                type="number"
                inputMode="numeric"
                min={2}
                max={8}
                value={groupCount}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") { setGroupCount(""); return; }
                  const n = parseInt(raw, 10);
                  if (!Number.isNaN(n) && n >= 0) setGroupCount(n);
                }}
                onBlur={() => { if (groupCount === "" || groupCount < 2) setGroupCount(2); }}
              />
            </div>
          ) : null}
        </div>

        {/* Match format (rule sets) */}
        <div>
          <p className="ds-label" style={{ marginBottom: "var(--ds-space-2)" }}>Match format</p>
          <div style={{
            display: "flex",
            gap: "var(--ds-space-2)",
            overflowX: "auto",
            scrollSnapType: "x mandatory",
            scrollbarWidth: "none",
            WebkitOverflowScrolling: "touch",
            paddingBottom: "var(--ds-space-1)",
          }}>
            {ruleSets.map((rs) => (
              <button
                key={rs.id}
                className="ds-option"
                aria-pressed={!custom && ruleSetId === rs.id}
                onClick={() => {
                  setRuleSetId(rs.id);
                  setCustom(false);
                }}
                style={{ flex: "none", minWidth: 90, scrollSnapAlign: "start" }}
              >
                <span className="ds-option__value" style={{ fontSize: "var(--ds-text-sm)" }}>{rs.name}</span>
              </button>
            ))}
            <button
              className="ds-option"
              aria-pressed={custom}
              onClick={() => {
                setCustom(true);
                const base = ruleSets[0];
                if (base && !ruleSetId) setRuleSetId(base.id);
              }}
              style={{ flex: "none", minWidth: 90, scrollSnapAlign: "start" }}
            >
              <span className="ds-option__value" style={{ fontSize: "var(--ds-text-sm)" }}>Custom</span>
            </button>
          </div>
          {!custom ? (
            <p className="ds-hint" style={{ marginTop: "var(--ds-space-2)" }}>
              {ruleSets.find((rs) => rs.id === ruleSetId)
                ? describeFormat(withoutId(ruleSets.find((rs) => rs.id === ruleSetId)!))
                : ""}
            </p>
          ) : (
            <div style={{ marginTop: "var(--ds-space-3)", display: "flex", flexDirection: "column", gap: "var(--ds-space-3)" }}>
              <p className="ds-hint">
                Each match in this tournament will use these rules. The scorer can still adjust per match.
              </p>
              <div style={{ display: "flex", gap: "var(--ds-space-3)" }}>
                <div className="ds-field" style={{ flex: 1 }}>
                  <label className="ds-label" htmlFor="t-overs">Overs</label>
                  <input
                    className="ds-input"
                    id="t-overs"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={customOvers}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (!Number.isNaN(v) && v > 0) {
                        setCustomOvers(v);
                        setCustomMaxPerBowler(suggestedMaxPerBowler(v) ?? 1);
                        const best = [...ruleSets].sort((a, b) =>
                          Math.abs((a.overs ?? 20) - v) - Math.abs((b.overs ?? 20) - v)
                        )[0];
                        if (best) setRuleSetId(best.id);
                      }
                    }}
                  />
                </div>
                <div className="ds-field" style={{ flex: 1 }}>
                  <label className="ds-label" htmlFor="t-players">Players a side</label>
                  <input
                    className="ds-input"
                    id="t-players"
                    type="number"
                    inputMode="numeric"
                    min={2}
                    value={customPlayers}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (!Number.isNaN(v) && v >= 2) setCustomPlayers(v);
                    }}
                  />
                </div>
                <div className="ds-field" style={{ flex: 1 }}>
                  <label className="ds-label" htmlFor="t-max-bowler">Max per bowler</label>
                  <input
                    className="ds-input"
                    id="t-max-bowler"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={customMaxPerBowler}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (!Number.isNaN(v) && v >= 1) setCustomMaxPerBowler(v);
                    }}
                  />
                </div>
              </div>
              <p className="ds-hint">
                Base format: {ruleSets.find((rs) => rs.id === ruleSetId)?.name ?? "—"} (closest match on the server)
              </p>
            </div>
          )}
        </div>

        {/* Public toggle */}
        <label className="ds-choice" style={{ marginTop: "var(--ds-space-1)" }}>
          <input
            type="checkbox"
            className="ds-switch"
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
          />
          Show on CricAI (public)
        </label>
      </div>

      {error ? <p className="ds-hint ds-hint--error" style={{ marginTop: "var(--ds-space-4)" }}>{error}</p> : null}

      <button
        className="ds-btn ds-btn--primary ds-btn--lg ds-btn--block"
        style={{ marginTop: "var(--ds-space-6)" }}
        disabled={!online || !name.trim() || !startDate || !endDate || (!ruleSetId && !custom) || submitting}
        onClick={() => void submit()}
      >
        {submitting ? "Creating…" : "Create tournament"}
      </button>
    </div>
  );
}
